import React, {useCallback, useEffect, useRef, useState} from "react";
import {Button} from "react-bootstrap";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import useAxiosPrivateFileUpload from "../../../../hooks/useAxiosPrivateFileUpload";
import {toastError, toastSuccess} from "../../../../helpers/Toast";

const MAX_FILE_SIZE = 8 * 1024 * 1024;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

const EmployeePhotoCard = ({employeeId}) => {
  const axiosPrivate = useAxiosPrivate();
  const axiosPrivateFileUpload = useAxiosPrivateFileUpload();
  const fileInputRef = useRef(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const replacePhotoUrl = useCallback((nextUrl) => {
    setPhotoUrl(previous => {
      if (previous) URL.revokeObjectURL(previous);
      return nextUrl;
    });
  }, []);

  const loadPhoto = useCallback(async () => {
    if (!employeeId) {
      replacePhotoUrl(null);
      return;
    }
    setLoading(true);
    try {
      const response = await axiosPrivate.get(`/employees/${employeeId}/photo`, {responseType: "blob"});
      replacePhotoUrl(URL.createObjectURL(response.data));
    } catch (error) {
      if (error?.response?.status === 404) {
        replacePhotoUrl(null);
      } else {
        toastError("Nie udało się pobrać zdjęcia pracownika");
      }
    } finally {
      setLoading(false);
    }
  }, [axiosPrivate, employeeId, replacePhotoUrl]);

  useEffect(() => {
    loadPhoto();
    return () => replacePhotoUrl(null);
  }, [loadPhoto, replacePhotoUrl]);

  const uploadPhoto = async event => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !employeeId) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toastError("Dozwolone formaty zdjęć to JPG, PNG i WEBP");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toastError("Zdjęcie nie może być większe niż 8 MB");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    setSaving(true);
    try {
      await axiosPrivateFileUpload.post(`/employees/${employeeId}/photo`, formData);
      await loadPhoto();
      toastSuccess("Zdjęcie pracownika zostało zapisane");
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się zapisać zdjęcia pracownika");
    } finally {
      setSaving(false);
    }
  };

  const deletePhoto = async () => {
    if (!employeeId || !photoUrl) return;
    setSaving(true);
    try {
      await axiosPrivate.delete(`/employees/${employeeId}/photo`);
      replacePhotoUrl(null);
      toastSuccess("Zdjęcie pracownika zostało usunięte");
    } catch (error) {
      toastError(error?.response?.data?.message || "Nie udało się usunąć zdjęcia pracownika");
    } finally {
      setSaving(false);
    }
  };

  if (!employeeId) {
    return (
      <div className="employee-span-4 employee-inline-info">
        Zapisz nowego pracownika, aby dodać jego zdjęcie.
      </div>
    );
  }

  return (
    <div className="employee-span-4 d-flex flex-wrap align-items-center gap-3">
      <div
        className="border rounded bg-light d-flex align-items-center justify-content-center overflow-hidden"
        style={{width: 128, height: 128}}
      >
        {loading ? (
          <span className="text-muted small">Ładowanie…</span>
        ) : photoUrl ? (
          <img src={photoUrl} alt="Zdjęcie pracownika" style={{width: "100%", height: "100%", objectFit: "cover"}}/>
        ) : (
          <span className="text-muted small text-center px-2">Brak zdjęcia</span>
        )}
      </div>
      <div className="d-flex flex-column gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="d-none"
          onChange={uploadPhoto}
        />
        <Button
          type="button"
          size="sm"
          variant="primary"
          disabled={saving}
          onClick={() => fileInputRef.current?.click()}
        >
          {photoUrl ? "Zmień zdjęcie" : "Dodaj zdjęcie"}
        </Button>
        {photoUrl && (
          <Button type="button" size="sm" variant="outline-danger" disabled={saving} onClick={deletePhoto}>
            Usuń zdjęcie
          </Button>
        )}
        <small className="text-muted">JPG, PNG lub WEBP, maksymalnie 8 MB.</small>
      </div>
    </div>
  );
};

export default EmployeePhotoCard;
